import React, { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import { BorderedContainer } from "components/BorderedContainer";
import { SolidButton } from "components/buttons/SolidButton";
import { useAppDispatch } from "store/hooks";
import { SimpleClientRegister } from "store/actions/authActions/SimpleClientRegister";
import { UserData } from "pages/SignUpPage";
import { SkipButton } from "./SkipButton";
import { LoginWithGoogle } from "store/actions/authActions/LoginWithGoogle";
import { LoginWithDiscord } from "store/actions/authActions/LoginWithDiscord";
import { AreYouReferredPrompt } from "./AreYouReferredPrompt";
import { useWindowSize } from "hooks/useWindowSize";
import { InputField } from "components/InputField";

type Props = {
  userData: UserData | null;
  setUserData: React.Dispatch<React.SetStateAction<UserData | null>>;
  qrTokenToValidate: string | undefined;
  setEmailInUseError: React.Dispatch<React.SetStateAction<boolean>>;
  emailInUseError: boolean;
  setForbiddenError: React.Dispatch<React.SetStateAction<boolean>>;
  forbiddenError: boolean;
  googleToken: string | null;
  discordCode: string | null;
};

type FormValues = {
  referrerEmail: string;
};

export const EnterFriendsEmailForm: React.FC<Props> = ({
  qrTokenToValidate,
  userData,
  setUserData,
  setEmailInUseError,
  setForbiddenError,
  emailInUseError,
  forbiddenError,
  googleToken,
  discordCode,
}) => {
  const [submitButtonIsLoading, setSubmitButtonIsLoading] =
    useState<boolean>(false);
  const [skipButtonIsLoading, setSkipButtonIsLoading] =
    useState<boolean>(false);
  const [signInRedirect, setSignInRedirect] = useState<boolean>(false);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const { t } = useTranslation();
  const language = t("localization_testing");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [width] = useWindowSize();
  const schema = yup.object().shape({
    referrerEmail: yup
      .string()
      .matches(/^[^<>]*$/, t("error.invalidCharMealName"))
      .max(100, t("form.emailTooLong"))
      .required(t("form.validEmail")),
  });
  const referrerCode = localStorage.getItem("referrerCode");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (emailInUseError || forbiddenError) {
      setUserData(null);
    }
  }, [emailInUseError, forbiddenError, setUserData]);

  if (signInRedirect && !!qrTokenToValidate)
    return <Navigate replace to="/done" />;
  if (signInRedirect && !qrTokenToValidate && width <= 640)
    return <Navigate replace to="/confirmation" />;
  if (signInRedirect && !qrTokenToValidate && width > 640)
    return <Navigate replace to="/dashboard/mymeals" />;

  return (
    <div className="h-screen md:h-auto w-full">
      {showTerms && (
        <BorderedContainer className="text-slate-200 w-full text-center md:w-2/3 lg:w-3/5 xl:w-2/5 mx-auto">
          <div className="absolute "></div>
          <div className="flex flex-col space-y-4 px-1 w-full">
            <h1 className="text-blue-500 text-lg pt-6">
              {t("refer.conditions")}
            </h1>
            <span>{t("refer.limit")}</span>
            <span>{t("refer.referredPerson")}</span>
            <span>{t("refer.discounts")}</span>
          </div>
          <div className="w-full flex justify-between pt-6 pr-1">
            <button
              type="button"
              className="hover:underline hover:text-blue-500 pointer-cursor pl-1"
              onClick={() => setShowTerms(false)}
            >
              {t("form.back")}
            </button>
            <SkipButton
              googleToken={googleToken}
              discordCode={discordCode}
              skipButtonIsLoading={skipButtonIsLoading}
              setSkipButtonIsLoading={setSkipButtonIsLoading}
              qrTokenToValidate={qrTokenToValidate}
              setUserData={setUserData}
              setForbiddenError={setForbiddenError}
              userData={userData}
              setEmailInUseError={setEmailInUseError}
              setRedirect={setSignInRedirect}
            />
          </div>
        </BorderedContainer>
      )}
      {!showTerms && (
        <BorderedContainer className="text-slate-200  w-full md:w-2/3 lg:w-3/5 xl:w-2/5 mx-auto">
          {!showForm && (
            <AreYouReferredPrompt
              setShowForm={setShowForm}
              userData={userData}
              setEmailInUseError={setEmailInUseError}
              setUserData={setUserData}
              setSkipButtonIsLoading={setSkipButtonIsLoading}
              qrTokenToValidate={qrTokenToValidate}
              setForbiddenError={setForbiddenError}
              skipButtonIsLoading={skipButtonIsLoading}
              setSignInRedirect={setSignInRedirect}
              googleToken={googleToken}
              discordCode={discordCode}
            />
          )}
          {showForm && (
            <form
              onSubmit={handleSubmit((data) => {
                localStorage.setItem("hasRefferalDiscount", "true");
                setSubmitButtonIsLoading(true);
                if (!!googleToken) {
                  console.log("login with google hit");
                  dispatch(
                    LoginWithGoogle(
                      language,
                      googleToken,
                      true,
                      true,
                      navigate,
                      setSignInRedirect,
                      setSubmitButtonIsLoading,
                      qrTokenToValidate,
                      data.referrerEmail
                    )
                  );
                }
                if (!!discordCode) {
                  dispatch(
                    LoginWithDiscord(
                      language,
                      discordCode,
                      true,
                      true,
                      setSignInRedirect,
                      navigate,
                      qrTokenToValidate,
                      data.referrerEmail
                    )
                  );
                  return setSubmitButtonIsLoading(false);
                }
                if (!!userData) {
                  dispatch(
                    SimpleClientRegister(
                      userData.firstName,
                      userData.lastName,
                      userData.email,
                      userData.password,
                      setSubmitButtonIsLoading,
                      setEmailInUseError,
                      setForbiddenError,
                      setSignInRedirect,
                      navigate,
                      dispatch,
                      language,
                      width,
                      qrTokenToValidate,
                      data.referrerEmail
                    )
                  );
                }
              })}
              className="px-4 lg:px-0"
            >
              <div className="mx-auto flex flex-col pt-2 px-6 mx-3/4 justify-center items-center ">
                <span className="text-blue-500 pb-2 text-xl font-semibold">
                  {t("refer.referralProgram")}
                </span>
                <div className="text-center flex flex-col text-sm space-x-4">
                  <span className="pb-2">{t("refer.referedByFriend?")}</span>
                  <span>{t("refer.instructions1")}</span>
                  <span className="pt-2 font-semibold">
                    {t("refer.instructions2")}
                  </span>
                </div>
              </div>
              <div className="pt-4 pb-2 px-4 md:px-0 md:w-3/4 lg:w-4/5 mx-auto">
                <div className="w-full">
                  <InputField
                    register={register("referrerEmail")}
                    id="email"
                    label={t("refer.enterAddress")}
                    placeholder={t("refer.enterAddressPlaceHolder")}
                    defaultValue={!!referrerCode ? referrerCode : ""}
                  />
                </div>
                {!!errors?.referrerEmail && (
                  <label htmlFor="email" className="text-red-500 text-xs">
                    {errors?.referrerEmail?.message}
                  </label>
                )}
              </div>
              <div className="flex flex-col justify-center py-4 items-center">
                <SolidButton
                  loading={submitButtonIsLoading}
                  type="submit"
                  width="w-40"
                  squared
                >
                  {t("refer.getDiscount")}
                </SolidButton>
                <button
                  type="button"
                  className="text-blue-500 text-sm pt-2 hover:underline hover:text-sky-400"
                  onClick={() => setShowTerms(true)}
                >
                  <span className="">{t("refer.seeTerms")}</span>
                </button>
              </div>
              <div className="flex justify-end -mt-5 pt-1">
                <SkipButton
                  setRedirect={setSignInRedirect}
                  googleToken={googleToken}
                  discordCode={discordCode}
                  skipButtonIsLoading={skipButtonIsLoading}
                  setSkipButtonIsLoading={setSkipButtonIsLoading}
                  qrTokenToValidate={qrTokenToValidate}
                  setUserData={setUserData}
                  setForbiddenError={setForbiddenError}
                  userData={userData}
                  setEmailInUseError={setEmailInUseError}
                />
              </div>
            </form>
          )}
        </BorderedContainer>
      )}
    </div>
  );
};
